import { useEffect, useMemo, useRef, useState } from 'react';

import { agentPromptParams, projectPromptParams } from '@playbooks/api';
import {
	createAgentErrorStream,
	formatAgentProjectHref,
	formatAgentProjectName,
	getConversationMessageText,
} from '@playbooks/utils/agents';
import { useTypewriterPlaceholder } from '@playbooks/wrappers/typewriter';

import { AgentPrompts } from 'components/agent/agent-prompts';
import { AgentRuntimeProvider } from 'components/agent/agent-runtime-provider';
import { Composer } from 'components/composer/composer';
import { ComposerTypes } from 'components/composer/composer-types';
import { useToast } from 'contexts';
import { createProjectOperatorMessageRun } from 'services/project-operator/project-operator-requests';
import { createTerminalProjectOperatorRunStream } from 'services/project-operator/project-operator-transport';
import { parseAgentUrls } from 'utils/agent-urls';

const ComposerWrapper = ({
	workspace,
	router,
	store,
	types = [],
	entity = 'project',
	onSend = undefined,
	onProjectCreated = undefined,
	beforeProjectCreate = undefined,
	showFocusRing = true,
}) => {
	const [inferenceModel, setInferenceModel] = useState<any>({ name: 'Model' });
	const [draftPreferences, setDraftPreferences] = useState<any>({});
	const [draftOverrides, setDraftOverrides] = useState<any>({});
	const [draftConnectors, setDraftConnectors] = useState<any[]>([]);
	const [planMode, setPlanMode] = useState(false);
	const [deliveryMode, setDeliveryMode] = useState('queue');
	const [type, setType] = useState<any>(types[0] || {});
	const [targets, setTargets] = useState<any>({});
	const [selectedTargets, setSelectedTargets] = useState<any[]>([]);
	const [conversation, setConversation] = useState<any>(null);
	const [intentConfirmation, setIntentConfirmation] = useState<any>(null);
	const preflightOverrideRef = useRef(null);
	const preflightIdRef = useRef(null);
	const preflightSurfaceRef = useRef('home');
	const toast = useToast();
	const draftKey =
		workspace.workspace?.uuid && type.uuid ? `home-${entity}-draft:${workspace.workspace.uuid}:${type.uuid}` : '';
	const resetDraftConfiguration = () => {
		setDraftPreferences({
			permissions: 'autonomous',
			designId: null,
			skills: [],
			mcps: [],
		});
		setInferenceModel({ name: 'Model' });
		setPlanMode(false);
		setDeliveryMode('queue');
		setDraftOverrides({});
		setDraftConnectors([]);
		if (draftKey) window.localStorage.removeItem(draftKey);
	};
	const onDraftPreferencesChange = next => {
		const changed = Object.keys(next).filter(
			key => JSON.stringify(next[key]) !== JSON.stringify(draftPreferences[key]),
		);
		setDraftOverrides(value => ({
			...value,
			...Object.fromEntries(changed.map(key => [key, next[key]])),
		}));
		setDraftPreferences(next);
	};

	// Computed
	const placeholders = type.placeholders || [];
	const placeholderSuffix = useTypewriterPlaceholder({ words: placeholders });
	const placeholderArticle = /^[aeiou]/i.test(placeholderSuffix.trim()) ? 'an' : 'a';
	const placeholder = placeholderSuffix
		? `Build me ${placeholderArticle} ${placeholderSuffix}`
		: entity === 'agent'
			? 'What should this agent do?'
			: 'What do you want to build?';
	const promptApi = useMemo(
		() => ({ ...(entity === 'agent' ? agentPromptParams : projectPromptParams), type: type.uuid }),
		[entity, type.uuid],
	);
	const runtimeId = `workspace-${workspace.workspace?.id || workspace.workspace?.uuid || 'home'}-composer`;
	const transport = useMemo(
		() => ({
			sendMessages: async ({ messages }) => {
				try {
					const userMessage = [...messages].reverse().find(message => message.role === 'user');
					const text = getConversationMessageText(userMessage).trim();
					if (!userMessage || !text) throw new Error('Please enter a message before sending.');
					let preflight = null;
					const preflightOverride = preflightOverrideRef.current;
					if (conversation?.uuid) {
						const response = await store.createRecord({
							url: `${workspace.rootApi}/operator-preflights`,
							data: {
								text,
								targets: selectedTargets,
								urls: parseAgentUrls(text).map(({ url, position }) => ({ url, position })),
								surface: preflightSurfaceRef.current,
								preflightOverride: preflightOverrideRef.current,
								preflightId: preflightIdRef.current,
							},
						});
						preflight = response.data;
						preflightIdRef.current = preflight.preflightId;
						if (preflight.needsClarification) {
							setIntentConfirmation({ text, ...preflight.clarification });
							return createTerminalProjectOperatorRunStream();
						}
						setIntentConfirmation(null);
					}
					if (preflight && preflight.route !== 'project_create') {
						await store.createRecord({
							url: `${workspace.rootApi}/conversations/${conversation.uuid}/messages`,
							data: {
								text,
								targets: selectedTargets,
								urls: parseAgentUrls(text).map(({ url, position }) => ({ url, position })),
								sourceType: 'home',
								preflightOverride,
								preflightId: preflightIdRef.current,
								preflightSurface: preflightSurfaceRef.current,
							},
						});
						preflightOverrideRef.current = null;
						preflightIdRef.current = null;
						preflightSurfaceRef.current = 'home';
						setSelectedTargets([]);
						await router.push({ pathname: router.pathname, query: { ...router.query, operator: '1' } }, undefined, {
							shallow: true,
						});
						return createTerminalProjectOperatorRunStream();
					}
					if (onSend) {
						await onSend({
							text,
							type,
							userMessage,
							inferenceModel,
							planMode,
							targets: selectedTargets,
							urls: parseAgentUrls(text),
							preferences: draftOverrides,
							connectors: draftConnectors,
						});
						setSelectedTargets([]);
						resetDraftConfiguration();
						return createTerminalProjectOperatorRunStream();
					}
					if (beforeProjectCreate) await beforeProjectCreate();

					const response = await store.saveRecord({
						url: `${workspace.rootApi}/projects`,
						params: { include: ['preferences.[model]'] },
						data: {
							name: formatAgentProjectName(text),
							projectTypeId: type.id,
							status: 'draft',
							visibility: 'public',
							preferences: {
								...(inferenceModel?.id ? { modelId: inferenceModel.id } : {}),
								...(planMode ? { mode: 'plan' } : {}),
								...Object.fromEntries(
									Object.entries(draftOverrides).filter(
										([key]) => !['designId', 'designResource', 'files', 'skills', 'tools', 'mcps'].includes(key),
									),
								),
							},
							resources: {
								...(Object.prototype.hasOwnProperty.call(draftOverrides, 'designId')
									? draftOverrides.designResource
										? { design: draftOverrides.designResource }
										: { designId: draftOverrides.designId }
									: {}),
								...(Object.prototype.hasOwnProperty.call(draftOverrides, 'skills')
									? {
											skillIds: (draftOverrides.skills || [])
												.filter(item => !item.pendingResource)
												.map(item => item.id),
											skills: (draftOverrides.skills || [])
												.filter(item => item.pendingResource)
												.map(item => item.pendingResource),
										}
									: {}),
								...(Object.prototype.hasOwnProperty.call(draftOverrides, 'tools')
									? { toolIds: (draftOverrides.tools || []).map(item => item.id) }
									: {}),
								...(Object.prototype.hasOwnProperty.call(draftOverrides, 'mcps')
									? {
											workspaceMcpIds: (draftOverrides.mcps || [])
												.filter(item => !item.pendingResource)
												.map(item => item.id),
											mcps: (draftOverrides.mcps || [])
												.filter(item => item.pendingResource)
												.map(item => item.pendingResource),
										}
									: {}),
								workspaceConnectorIds: draftConnectors.map(item => item.workspaceConnectorId),
							},
						},
					});
					const project = response.data;
					const branch = project.branch;
					if (!project?.uuid || !branch?.id) throw new Error('Project creation did not return an active branch.');

					const { run } = await createProjectOperatorMessageRun({
						rootApi: `${workspace.rootApi}/projects/${project.uuid}/branches/${branch.id}`,
						store,
						text,
						userMessage,
						targets: selectedTargets,
						mode: planMode ? 'plan' : 'execute',
						instance: project.preferences.model,
						preflightId: preflightIdRef.current,
						preflightOverride,
						preflightSurface: preflightSurfaceRef.current,
					});
					preflightOverrideRef.current = null;
					preflightIdRef.current = null;
					preflightSurfaceRef.current = 'home';

					if (onProjectCreated)
						await onProjectCreated({
							project,
							branch,
							text,
							userMessage,
							mode: planMode ? 'plan' : 'build',
							run,
						});
					else router.push(formatAgentProjectHref(project, branch));
					setSelectedTargets([]);
					resetDraftConfiguration();
					return createTerminalProjectOperatorRunStream();
				} catch (error) {
					if (error?.code?.startsWith?.('operator_preflight_')) {
						preflightIdRef.current = null;
						preflightOverrideRef.current = null;
						preflightSurfaceRef.current = 'home';
					}
					return createAgentErrorStream(error, ({ message }) => toast.showError(message));
				}
			},
		}),
		[
			workspace.rootApi,
			inferenceModel,
			onProjectCreated,
			onSend,
			beforeProjectCreate,
			planMode,
			draftOverrides,
			draftConnectors,
			router,
			store,
			toast,
			type,
			selectedTargets,
			conversation?.uuid,
		],
	);

	// Hooks
	useEffect(() => {
		if (!type.id && types.length) setType(types[0]);
	}, [types, type.id]);

	useEffect(() => {
		if (!draftKey) return;
		const value = window.localStorage.getItem(draftKey);
		if (!value) return resetDraftConfiguration();
		try {
			const draft = JSON.parse(value);
			const savedPreferences = Object.fromEntries(
				Object.entries(draft.preferences || {}).filter(([key]) => key !== 'files'),
			);
			const savedOverrides = Object.fromEntries(
				Object.entries(draft.overrides || {}).filter(([key]) => key !== 'files'),
			);
			setDraftPreferences({
				permissions: 'autonomous',
				designId: null,
				skills: [],
				mcps: [],
				...savedPreferences,
			});
			setDraftOverrides(savedOverrides);
			setDraftConnectors(draft.connectors || []);
			setInferenceModel(draft.model || { name: 'Model' });
			setPlanMode(Boolean(draft.planMode));
			setDeliveryMode(draft.deliveryMode || 'queue');
		} catch {
			resetDraftConfiguration();
		}
	}, [draftKey]);

	useEffect(() => {
		if (!draftKey) return;
		const resources = value =>
			Array.isArray(value)
				? value
						.filter(item => !item.pendingResource)
						.map(({ id, uuid, name, workspaceConnectorId }) => ({ id, uuid, name, workspaceConnectorId }))
				: value;
		const overrides = Object.fromEntries(Object.entries(draftOverrides).map(([key, value]) => [key, resources(value)]));
		const meaningful =
			Boolean(inferenceModel?.id) ||
			planMode ||
			deliveryMode !== 'queue' ||
			Object.keys(overrides).length > 0 ||
			draftConnectors.length > 0;
		if (!meaningful) {
			window.localStorage.removeItem(draftKey);
			return;
		}
		window.localStorage.setItem(
			draftKey,
			JSON.stringify({
				preferences: {
					...draftPreferences,
					skills: resources(draftPreferences.skills),
					mcps: resources(draftPreferences.mcps),
				},
				overrides,
				connectors: resources(draftConnectors),
				model: inferenceModel?.id
					? { id: inferenceModel.id, uuid: inferenceModel.uuid, name: inferenceModel.name }
					: null,
				planMode,
				deliveryMode,
			}),
		);
	}, [draftKey, draftPreferences, draftOverrides, draftConnectors, inferenceModel, planMode, deliveryMode]);

	useEffect(() => {
		if (!workspace.rootApi) return;
		const controller = new AbortController();
		store
			.queryRecord({ url: `${workspace.rootApi}/conversation`, signal: controller.signal })
			.then(async response => {
				if (controller.signal.aborted || !response.data?.uuid) return;
				setConversation(response.data);
				const configuration = await store.queryRecord({
					url: `${workspace.rootApi}/conversations/${response.data.uuid}/workspace`,
					signal: controller.signal,
				});
				if (!controller.signal.aborted) setTargets(configuration.data?.targets || {});
			})
			.catch(error => error?.name !== 'AbortError' && setTargets({}));
		return () => controller.abort();
	}, [store, workspace.rootApi]);

	// Render
	return (
		<div className='mx-auto w-full max-w-xl'>
			<AgentRuntimeProvider runtimeId={runtimeId} transport={transport}>
				<Composer
					canManageProjectDefaults={Boolean(
						workspace.workspace?.access?.capabilities?.canManageProjectDefaults ??
						workspace.workspace?.owner ??
						workspace.type === 'personal',
					)}
					preferences={draftPreferences}
					connectors={draftConnectors}
					onDraftPreferencesChange={onDraftPreferencesChange}
					onDraftConnectorsChange={setDraftConnectors}
					model={inferenceModel}
					onModelSelect={setInferenceModel}
					placeholder={placeholder}
					showFocusRing={showFocusRing}
					planMode={planMode}
					onPlanModeChange={value => {
						setPlanMode(value);
						onDraftPreferencesChange({ ...draftPreferences, mode: value ? 'plan' : 'execute' });
					}}
					deliveryMode={deliveryMode}
					onDeliveryModeChange={setDeliveryMode}
					targets={targets}
					selectedTargets={selectedTargets}
					onTargetSelect={target => setSelectedTargets(current => [...current, target])}
					onTargetRemove={target => setSelectedTargets(current => current.filter(value => value !== target))}
					intentConfirmation={intentConfirmation}
					onIntentChoose={intent => {
						preflightOverrideRef.current = intent;
						setIntentConfirmation(null);
					}}
					onIntentDismiss={() => {
						preflightOverrideRef.current = null;
						preflightIdRef.current = null;
						preflightSurfaceRef.current = 'home';
						setIntentConfirmation(null);
					}}
				/>
				<div className='mt-3 flex w-full items-center gap-2'>
					<ComposerTypes type={type} types={types} entity={entity} onSelect={setType} />
					<AgentPrompts
						api={promptApi}
						model={type}
						rootApi={entity === 'agent' ? '/agent-prompts' : '/project-prompts'}
					/>
				</div>
			</AgentRuntimeProvider>
		</div>
	);
};

export { ComposerWrapper };
